import { Group, Tooltip } from '@mantine/core';
import { Icon } from '@repo/foundations';
import type React from 'react';
import { useCallback, useRef, useState } from 'react';
import type { ISecodaEditor } from '../..';
import dictionary from '../../dictionary';
import { Toolbar } from '../../types';
import { FloatingToolbar } from './FloatingToolbar';
import Input from './Input';
import ToolbarButton from './ToolbarButton';
import Separator from './ToolbarSeparator';

interface PlaceholderToolbarProps {
	editor: ISecodaEditor;
}

export function PlaceholderToolbar({ editor }: PlaceholderToolbarProps) {
	const menuRef = useRef<HTMLDivElement>(null);
	const [value, setValue] = useState('');

	const close = useCallback(() => {
		editor.closeToolbars(Toolbar.Placeholder);
		editor.view?.focus();
		setValue('');
	}, [editor]);

	const handleKeyDown = (event: React.KeyboardEvent): void => {
		const { view } = editor;

		if (!view) {
			return;
		}

		// eslint-disable-next-line default-case
		switch (event.key) {
			case 'Enter': {
				event.preventDefault();

				view.dispatch(
					view.state.tr
						.insertText(
							value,
							view.state.selection.from,
							view.state.selection.to
						)
						.addMark(
							view.state.selection.from,
							view.state.selection.to + value.length,
							view.state.schema.marks.placeholder.create()
						)
				);

				close();
				break;
			}

			case 'Escape': {
				event.preventDefault();
				close();
				break;
			}
		}
	};

	return (
		<FloatingToolbar
			ref={menuRef}
			view={editor.view!}
			onClose={() => editor.closeToolbars(Toolbar.Placeholder)}
		>
			<Group spacing="3xs">
				<Input
					value={value}
					placeholder={dictionary.placeholderInput}
					onKeyDown={handleKeyDown}
					onChange={(ev) => setValue(ev.currentTarget.value)}
					autoFocus
				/>
				<Separator />
				<Tooltip label={dictionary.close} position="top">
					<ToolbarButton onClick={close}>
						<Icon name="trash" />
					</ToolbarButton>
				</Tooltip>
			</Group>
		</FloatingToolbar>
	);
}
